/**
 * Next.js App Component
 * Global app wrapper with AuthProvider and Layout
 *
 * Features:
 * - AuthProvider wrapper for global auth state
 * - Layout component for navigation
 * - Protected route checks
 * - Global error handling
 * - Session persistence
 */

'use client'

import React, { useEffect, useContext } from 'react'
import { useRouter } from 'next/router'
import { AuthProvider, useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'
import LoadingSpinner from '../components/LoadingSpinner'
import '../styles/globals.css'

// Pages that don't require authentication
const PUBLIC_PAGES = ['/login', '/register', '/']

// Pages that require authentication
const PROTECTED_PAGES = ['/dashboard', '/admin']

/**
 * App Content Component
 * Handles routing logic and page rendering
 */
function AppContent({ Component, pageProps }) {
  const router = useRouter()
  const { user, isAuthenticated, isInitialized, logout } = useAuth()

  // Check if current page is protected
  const isProtectedPage = PROTECTED_PAGES.includes(router.pathname)
  const isPublicPage = PUBLIC_PAGES.includes(router.pathname)

  // Check admin access for admin page
  const isAdminPage = router.pathname === '/admin'
  const isAdmin = user?.tier === 'admin'

  // Redirect to login if accessing protected page without auth
  useEffect(() => {
    if (!isInitialized) return

    if (isProtectedPage && !isAuthenticated) {
      router.push('/login')
      return
    }

    // Redirect admin page to dashboard with usage section
    if (isAdminPage && isAdmin) {
      router.push('/dashboard?section=usage')
      return
    }

    // Redirect to dashboard if admin page but not admin
    if (isAdminPage && !isAdmin) {
      router.push('/dashboard')
      return
    }

    // Redirect to dashboard if already logged in and on public page
    if (isPublicPage && isAuthenticated && router.pathname !== '/') {
      router.push('/dashboard')
      return
    }
  }, [isProtectedPage, isPublicPage, isAuthenticated, isAdmin, isAdminPage, router, isInitialized])

  // Loading state while checking auth
  if (!isInitialized) {
    return (
      <div className="sc-auth-page">
        <LoadingSpinner message="> initializing process..." size="large" />
      </div>
    )
  }

  // Render page with Layout
  return (
    <Layout user={user} onLogout={logout}>
      <Component {...pageProps} />
    </Layout>
  )
}

/**
 * Main App Component
 * Wraps everything with AuthProvider
 */
export default function App({ Component, pageProps }) {
  return (
    <AuthProvider>
      <AppContent Component={Component} pageProps={pageProps} />
    </AuthProvider>
  )
}
